import React, { useState, useEffect } from 'react';
import { Chart as ChartJS, ArcElement, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend } from 'chart.js';
import { Doughnut, Bar } from 'react-chartjs-2';
import dashboardService from '../../services/dashboardService';
import { FiTrendingUp, FiDollarSign, FiUsers, FiFolder, FiAlertCircle, FiCheckCircle } from 'react-icons/fi';
import { FaChevronDown, FaChevronUp, FaIndianRupeeSign } from "react-icons/fa6";
import { CURRENCY_SYMBOL } from '../../config/constants';
import projectService from '../../services/projectService';

ChartJS.register(ArcElement, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);


export const icons = {
  inr: FaIndianRupeeSign,
};

const ExecutiveDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const Inr = CURRENCY_SYMBOL
  const [projects, setProjects] = useState([]);
  const [isProjectOpen, setIsProjectOpen] = useState(false);
  const [filters, setFilters] = useState({ project_id: '' });

  useEffect(() => {
    loadData();
    fetchProjects();
  }, []);

  useEffect(() => {
  loadData();
}, [filters.project_id]);

  const fetchProjects = async () => {
    try {
      const data = await projectService.getAllProjects();
      setProjects(data.projects || []);
    } catch (err) {
      console.error('Failed to fetch projects:', err);
    }
  };

  const loadData = async () => {
    try {
      setLoading(true);
      const result = await dashboardService.getExecutiveDashboard(filters.project_id);
      setData(result);
    } catch (error) {
      console.error('Error loading executive dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-xl text-gray-600">Loading executive dashboard...</div>
      </div>
    );
  }

  const projectHealthData = {
    labels: ['On Track', 'At Risk', 'Delayed', 'Completed'],
    datasets: [{
      data: [
        data?.projectHealth?.onTrack || 0,
        data?.projectHealth?.atRisk || 0,
        data?.projectHealth?.delayed || 0,
        data?.projectHealth?.completed || 0
      ],
      backgroundColor: ['#10B981', '#F59E0B', '#EF4444', '#3B82F6'],
      borderWidth: 2,
      borderColor: '#fff'
    }]
  };

  const taskMetricsData = {
    labels: ['Completed', 'In Progress', 'Blocked'],
    datasets: [{
      label: 'Tasks',
      data: [
        data?.taskMetrics?.completed || 0,
        data?.taskMetrics?.inProgress || 0,
        data?.taskMetrics?.blocked || 0
      ],
      backgroundColor: ['#10B981', '#3B82F6', '#EF4444'],
      borderRadius: 8,
    }]
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          padding: 15,
          font: { size: 12 }
        }
      }
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Executive Dashboard</h1>
          <p className="text-gray-600 mt-1">High-level overview of organizational performance</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="relative w-64">
            <select
              name="project_id"
              value={filters.project_id}
              onChange={(e) => setFilters(prev => ({ ...prev, project_id: e.target.value }))}
              onFocus={() => setIsProjectOpen(true)}
              onBlur={() => setIsProjectOpen(false)}
              className="w-full appearance-none rounded-lg border border-gray-300 bg-white px-4 py-2.5 pr-10 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
            >
              <option value="">All Projects</option>
              {projects.map((project) => (
                <option key={project.id} value={project.id}>
                  {project.name}
                </option>
              ))}
            </select>

            <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center">
              {isProjectOpen ? <FaChevronUp /> : <FaChevronDown />}
            </div>
          </div>
          <button
            // onClick={loadData}
            onClick={() => setFilters({ project_id: '' })}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            🔄 Refresh
          </button>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white p-6 rounded-xl shadow-lg border-l-4 border-blue-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-600 text-sm font-medium">Total Projects</p>
              <p className="text-3xl font-bold text-gray-900 mt-2">{data?.overview?.totalProjects || 0}</p>
            </div>
            <FiFolder className="w-12 h-12 text-blue-500 " />
          </div>
        </div>

        <div className="bg-white p-6 rounded-xl shadow-lg border-l-4 border-green-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-600 text-sm font-medium">Total Budget</p>
              <p className="text-3xl font-bold text-gray-900 mt-2 d-flex
              ">
                <span className="fs-2" style={{ fontSize: "1.5rem", marginTop: "0.5rem" }} > <Inr /></span>{(data?.overview?.totalBudget || 0).toLocaleString()}
              </p>
            </div>
            <Inr className="w-12 h-12 text-green-500 " />
          </div>
        </div>

        <div className="bg-white p-6 rounded-xl shadow-lg border-l-4 border-purple-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-600 text-sm font-medium">Budget Utilization</p>
              <p className="text-3xl font-bold text-gray-900 mt-2">{data?.overview?.budgetUtilization || 0}%</p>
            </div>
            <FiTrendingUp className="w-12 h-12 text-purple-500 " />
          </div>
        </div>

        <div className="bg-white p-6 rounded-xl shadow-lg border-l-4 border-orange-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-600 text-sm font-medium">Team Size</p>
              <p className="text-3xl font-bold text-gray-900 mt-2">{data?.overview?.teamSize || 0}</p>
            </div>
            <FiUsers className="w-12 h-12 text-orange-500 " />
          </div>
        </div>
      </div>

      {/* Financial Summary */}
      <div className="bg-white p-6 rounded-xl shadow-lg">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Financial Summary</h2>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="p-4 bg-blue-50 rounded-lg">
            <p className="text-sm text-gray-600">Total Budget</p>
            <p className="text-2xl font-bold text-blue-600 d-flex">
              <span className="fs-2" style={{ fontSize: "1.5rem", marginTop: "0.5rem" }} > <Inr /></span>{(data?.financialSummary?.budget || 0).toLocaleString()}
            </p>
          </div>
          <div className="p-4 bg-red-50 rounded-lg custom-red">
            <p className="text-sm text-gray-600 custom-red-title">Total Spent</p>
            <p className="text-2xl font-bold text-red-600 d-flex custom-red-title">
              <span className="fs-2" style={{ fontSize: "1.5rem", marginTop: "0.5rem" }} > <Inr /></span>{(data?.financialSummary?.spent || 0).toLocaleString()}
            </p>
          </div>
          <div className="p-4 bg-green-50 rounded-lg custom-red">
            <p className="text-sm text-gray-600 custom-red-title" >Remaining</p>
            <p className="text-2xl font-bold text-green-600 d-flex custom-red-title">
              <span className="fs-2" style={{ fontSize: "1.5rem", marginTop: "0.5rem" }} > <Inr /></span>{(data?.financialSummary?.remaining || 0).toLocaleString()}
            </p>
          </div>
          <div className="p-4 bg-purple-50 rounded-lg custom-red">
            <p className="text-sm text-gray-600 custom-red-title">Utilization Rate</p>
            <p className="text-2xl font-bold text-purple-600 custom-red-title">
              {data?.financialSummary?.utilizationRate || 0}%
            </p>
          </div>
        </div>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Project Health */}
        <div className="bg-white p-6 rounded-xl shadow-lg">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Project Health</h2>
          <div className="h-80">
            <Doughnut data={projectHealthData} options={chartOptions} />
          </div>
        </div>

        {/* Task Metrics */}
        <div className="bg-white p-6 rounded-xl shadow-lg">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Task Metrics</h2>
          <div className="h-80">
            <Bar data={taskMetricsData} options={chartOptions} />
          </div>
        </div>
      </div>

      {/* Project Health Details */}
      <div className="bg-white p-6 rounded-xl shadow-lg">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Project Status Breakdown</h2>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="flex items-center space-x-3 p-4 bg-green-50 rounded-lg custom-red">
            <FiCheckCircle className="w-8 h-8 text-green-600" />
            <div className="">
              <p className="text-sm text-gray-600 custom-red-title">On Track</p>
              <p className="text-2xl font-bold text-green-600 custom-red-title">{data?.projectHealth?.onTrack || 0}</p>
            </div>
          </div>
          <div className="flex items-center space-x-3 p-4 bg-yellow-50 rounded-lg custom-red">
            <FiAlertCircle className="w-8 h-8 text-yellow-600" />
            <div>
              <p className="text-sm text-gray-600 custom-red-title">At Risk</p>
              <p className="text-2xl font-bold text-yellow-600 custom-red-title">{data?.projectHealth?.atRisk || 0}</p>
            </div>
          </div>
          <div className="flex items-center space-x-3 p-4 bg-red-50 rounded-lg custom-red">
            <FiAlertCircle className="w-8 h-8 text-red-600" />
            <div>
              <p className="text-sm text-gray-600 custom-red-title">Delayed</p>
              <p className="text-2xl font-bold text-red-600 custom-red-title">{data?.projectHealth?.delayed || 0}</p>
            </div>
          </div>
          <div className="flex items-center space-x-3 p-4 bg-blue-50 rounded-lg">
            <FiCheckCircle className="w-8 h-8 text-blue-600" />
            <div>
              <p className="text-sm text-gray-600">Completed</p>
              <p className="text-2xl font-bold text-blue-600">{data?.projectHealth?.completed || 0}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Task Summary */}
      <div className="bg-white p-6 rounded-xl shadow-lg">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Task Summary</h2>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="p-4 bg-gray-50 rounded-lg">
            <p className="text-sm text-gray-600">Total Tasks</p>
            <p className="text-2xl font-bold text-gray-900">{data?.taskMetrics?.total || 0}</p>
          </div>
          <div className="p-4 bg-green-50 rounded-lg custom-red">
            <p className="text-sm text-gray-600 custom-red-title">Completed</p>
            <p className="text-2xl font-bold text-green-600 custom-red-title">{data?.taskMetrics?.completed || 0}</p>
          </div>
          <div className="p-4 bg-blue-50 rounded-lg">
            <p className="text-sm text-gray-600">In Progress</p>
            <p className="text-2xl font-bold text-blue-600">{data?.taskMetrics?.inProgress || 0}</p>
          </div>
          <div className="p-4 bg-red-50 rounded-lg custom-red">
            <p className="text-sm text-gray-600 custom-red-title">Blocked</p>
            <p className="text-2xl font-bold text-red-600 custom-red-title">{data?.taskMetrics?.blocked || 0}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExecutiveDashboard;

